import cuid from "cuid";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineBadge,
  MdOutlineEditCalendar,
  MdOutlinePayment,
  MdOutlinePerson,
  MdOutlinePinDrop,
  MdOutlineQrCode,
  MdOutlineReplay,
  MdOutlineSouthAmerica,
  MdPeopleOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ATTACHMENT_TYPES,
  BANK_ACCOUNT_OWNER_TYPE,
  CONTACT_POINT_SYSTEM,
  GENDER,
  LMF_DEBIT_POSSIBLE_DATES,
  PAYMENT_FREQUENCIES,
  PAYMENT_TYPES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { setDefaultIfEmpty } from "utils/helpers/input";

const Payment = ({
  disabled,
  setDisabled,
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const PAYMENT_PAYER = {
    [true]: () => {
      set(initialValues, "payment.payer.owner", {
        id: get(initialValues, "payment.payer.owner.id", cuid()),
        gender: get(initialValues, "project.contact.user.gender"),
        firstName: get(initialValues, "project.contact.user.firstname"),
        lastName: get(initialValues, "project.contact.user.lastname"),
        isAdherent: true,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      });
      set(
        initialValues,
        "telecoms.payer",
        get(initialValues, "telecoms.contact", []),
      );
      const newDisabled = {
        ...disabled,
        proof: {
          ...disabled.proof,
          payer: {
            ...disabled.proof?.payer,
            [ATTACHMENT_TYPES.PAYER_CIN]: true,
            [ATTACHMENT_TYPES.KBIS]: true,
            [ATTACHMENT_TYPES.PAYER_RIB]: true,
          },
        },
        [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: true,
        payment: {
          ...disabled.payment,
          payer: {
            ...disabled.payment?.payer,
            owner: {
              ...disabled.payment?.payer?.owner,
              socialReason: true,
              type: true,
              gender: true,
              firstName: true,
              lastName: true,
            },
          },
        },
        telecoms: {
          ...disabled.telecoms,
          payer: {
            [CONTACT_POINT_SYSTEM.STREET]: { value: true },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: true },
            [CONTACT_POINT_SYSTEM.CITY]: { value: true },
          },
          [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: true,
        },
      };
      setDisabled(newDisabled);
      return newDisabled;
    },
    [false]: () => {
      set(initialValues, "payment.payer.owner", {
        id: get(initialValues, "payment.payer.owner.id", cuid()),
        isAdherent: false,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      });
      set(initialValues, "telecoms.payer", {});
      const newDisabled = {
        ...disabled,
        proof: {
          ...disabled.proof,
          payer: {
            ...disabled.proof?.payer,
            [ATTACHMENT_TYPES.PAYER_CIN]: true,
            [ATTACHMENT_TYPES.KBIS]: true,
            [ATTACHMENT_TYPES.PAYER_RIB]: false,
          },
        },
        [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: {
          [ATTACHMENT_TYPES.PAYER_CIN]: true,
          [ATTACHMENT_TYPES.KBIS]: true,
          [ATTACHMENT_TYPES.PAYER_RIB]: false,
        },
        payment: {
          ...disabled.payment,
          payer: {
            ...disabled.payment?.payer,
            owner: {
              ...disabled.payment?.payer?.owner,
              socialReason: true,
              gender: false,
              firstName: false,
              lastName: false,
            },
          },
        },
        telecoms: {
          ...disabled.telecoms,
          payer: undefined,
        },
      };
      setDisabled(newDisabled);
      return newDisabled;
    },
  };

  const clearPayerFields = () => {
    set(initialValues, "payment.payer.owner.socialReason", null);
    set(initialValues, "payment.payer.owner.gender", null);
    set(initialValues, "payment.payer.owner.firstName", null);
    set(initialValues, "payment.payer.owner.lastName", null);
    set(
      initialValues,
      `telecoms.payer.${CONTACT_POINT_SYSTEM.STREET}.value`,
      null,
    );
    set(
      initialValues,
      `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
      null,
    );
    set(
      initialValues,
      `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`,
      null,
    );
  };

  const PAYMENT_PAYER_TYPE = {
    [BANK_ACCOUNT_OWNER_TYPE.PARTICULAR]: () => {
      clearPayerFields();
      const newDisabled = {
        ...disabled,
        proof: {
          ...disabled.proof,
          payer: {
            ...disabled.proof?.payer,
            [ATTACHMENT_TYPES.PAYER_CIN]: true,
            [ATTACHMENT_TYPES.KBIS]: true,
            [ATTACHMENT_TYPES.PAYER_RIB]: false,
          },
        },
        [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: {
          [ATTACHMENT_TYPES.PAYER_CIN]: true,
          [ATTACHMENT_TYPES.KBIS]: true,
          [ATTACHMENT_TYPES.PAYER_RIB]: false,
        },
        payment: {
          ...disabled.payment,
          payer: {
            ...disabled.payment?.payer,
            owner: {
              ...disabled.payment?.payer?.owner,
              socialReason: true,
              gender: false,
              firstName: false,
              lastName: false,
            },
          },
        },
      };
      setDisabled(newDisabled);
      return newDisabled;
    },
    [BANK_ACCOUNT_OWNER_TYPE.COMPANY]: () => {
      clearPayerFields();
      const newDisabled = {
        ...disabled,
        proof: {
          ...disabled.proof,
          payer: {
            ...disabled.proof?.payer,
            [ATTACHMENT_TYPES.PAYER_CIN]: false,
            [ATTACHMENT_TYPES.KBIS]: false,
            [ATTACHMENT_TYPES.PAYER_RIB]: false,
          },
        },
        [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: {
          [ATTACHMENT_TYPES.PAYER_CIN]: false,
          [ATTACHMENT_TYPES.KBIS]: false,
          [ATTACHMENT_TYPES.PAYER_RIB]: false,
        },
        payment: {
          ...disabled.payment,
          payer: {
            ...disabled.payment?.payer,
            owner: {
              ...disabled.payment?.payer?.owner,
              socialReason: false,
              gender: true,
              firstName: true,
              lastName: true,
            },
          },
        },
      };
      setDisabled(newDisabled);
      return newDisabled;
    },
  };

  const paymentTypePath = "payment.type";
  const paymentTypeField = bindField(paymentTypePath, {
    label: SUBSCRIPTION.FORM.PAYMENT_TYPE.LABEL,
    required: isFieldRequired(paymentTypePath),
    onBlur: () => setPercent(initialValues),
  });

  const paymentFrequencyPath = "payment.frequency";
  const paymentFrequencyField = bindField(paymentFrequencyPath, {
    label: SUBSCRIPTION.FORM.PAYMENT_FREQUENCY.LABEL,
    required: isFieldRequired(paymentFrequencyPath),
    onBlur: () => setPercent(initialValues),
  });

  const debitDayPath = "payment.debitDay";
  const debitDayField = bindField(debitDayPath, {
    label: SUBSCRIPTION.FORM.DEBIT_DATE,
    required: isFieldRequired(debitDayPath),
    onBlur: () => setPercent(initialValues),
  });

  const ibanPath = "payment.payer.IBAN";
  const ibanField = bindField(ibanPath, {
    label: SUBSCRIPTION.FORM.IBAN.LABEL,
    required: isFieldRequired(ibanPath),
    onBlur: () => setPercent(initialValues),
  });

  const bicPath = "payment.payer.BIC";
  const bicField = bindField(bicPath, {
    label: SUBSCRIPTION.FORM.BIC.LABEL,
    required: isFieldRequired(bicPath),
    onBlur: () => setPercent(initialValues),
  });

  const isAdherentPath = "payment.payer.owner.isAdherent";
  const isAdherentField = bindField(isAdherentPath, {
    label: SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.LABEL,
    required: isFieldRequired(isAdherentPath),
    disabled: get(
      initialValues,
      "insurancePlan.fields.payment.shouldBeAdherent",
    ),
    onChange: (value) => {
      const newDisabled = PAYMENT_PAYER[value]();
      setDisabled((prev) => ({
        ...prev,
        proof: {
          ...prev.proof,
          payerCin: value,
        },
      }));
      setPercent(initialValues, newDisabled);
    },
    onBlur: () => setPercent(initialValues),
  });

  const ownerTypePath = "payment.payer.owner.type";
  const ownerTypeField = bindField(ownerTypePath, {
    label: SUBSCRIPTION.FORM.BANK_ACCOUNT_OWNER_TYPE.LABEL,
    required: isFieldRequired(ownerTypePath),
    onChange: (value) => {
      const newDisabled = PAYMENT_PAYER_TYPE[value]();
      setPercent(initialValues, newDisabled);
    },
  });

  const socialReasonPath = "payment.payer.owner.socialReason";
  const socialReasonField = bindField(socialReasonPath, {
    label:
      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.ORGANIZATION
        .SOCIAL_REASON,
    required: isFieldRequired(socialReasonPath),
    onBlur: () => setPercent(initialValues),
  });

  const genderPath = "payment.payer.owner.gender";
  const genderField = bindField(genderPath, {
    label: SUBSCRIPTION.FORM.GENDER.LABEL,
    required: true,
    onChange: () => setPercent(initialValues),
  });

  const firstNamePath = "payment.payer.owner.firstName";
  const firstNameField = bindField(firstNamePath, {
    label:
      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.IDENTITY.FIRST_NAME
        .LABEL,
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const lastNamePath = "payment.payer.owner.lastName";
  const lastNameField = bindField(lastNamePath, {
    label:
      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.IDENTITY.LAST_NAME
        .LABEL,
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  const streetPath = `telecoms.payer.${CONTACT_POINT_SYSTEM.STREET}.value`;
  const streetField = bindField(streetPath, {
    label: SUBSCRIPTION.FORM.STREET.LABEL,
    required: isFieldRequired(streetPath),
    onBlur: () => setPercent(initialValues),
  });

  const zipCodePath = `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`;
  const zipCodeField = bindField(zipCodePath, {
    label: SUBSCRIPTION.FORM.POSTAL_CODE.LABEL,
    required: isFieldRequired(zipCodePath),
    onBlur: () => setPercent(initialValues),
  });

  const cityPath = `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`;
  const cityField = bindField(cityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(cityPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PAYMENT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.TITLE}
      >
        <div className="grid--1">
          <FormField
            path={paymentTypeField.path}
            label={paymentTypeField.label}
            required={paymentTypeField.required}
            disabled={paymentTypeField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlinePayment size={16} />}
                label={paymentTypeField.label}
                values={setDefaultIfEmpty(
                  get(initialValues, "insurancePlan.fields.payment.type"),
                  Object.values(PAYMENT_TYPES),
                ).map((value) => ({
                  value,
                  label: SUBSCRIPTION.FORM.PAYMENT_TYPE.OPTIONS[value],
                }))}
                value={get(initialValues, paymentTypePath)}
                {...field}
                onBlur={paymentTypeField.onBlur}
                onChange={paymentTypeField.onChange}
              />
            )}
          </FormField>
          <FormField
            path={paymentFrequencyField.path}
            label={paymentFrequencyField.label}
            required={paymentFrequencyField.required}
            disabled={paymentFrequencyField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlineReplay size={16} />}
                label={paymentFrequencyField.label}
                values={setDefaultIfEmpty(
                  get(initialValues, "insurancePlan.fields.payment.frequency"),
                  Object.values(PAYMENT_FREQUENCIES),
                ).map((value) => ({
                  value,
                  label: SUBSCRIPTION.FORM.PAYMENT_FREQUENCY.OPTIONS[value],
                }))}
                value={get(initialValues, paymentFrequencyPath)}
                {...field}
                onBlur={paymentFrequencyField.onBlur}
                onChange={paymentFrequencyField.onChange}
              />
            )}
          </FormField>
          <FormField
            path={debitDayField.path}
            label={debitDayField.label}
            required={debitDayField.required}
            disabled={debitDayField.disabled}
          >
            {(field) => (
              <Select
                options={LMF_DEBIT_POSSIBLE_DATES.map((value) => ({
                  value,
                  label: value,
                }))}
                placeholder={SUBSCRIPTION.FORM.DEBIT_DATE}
                label={debitDayField.label}
                icon={<MdOutlineEditCalendar size={16} />}
                defaultValue={get(initialValues, debitDayPath) || "5"}
                {...field}
                onChange={debitDayField.onChange}
                onBlur={debitDayField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={ibanField.path}
            label={ibanField.label}
            required={ibanField.required}
            disabled={ibanField.disabled}
          >
            {(field) => (
              <Input
                label={ibanField.label}
                icon={<MdOutlineQrCode size={16} />}
                type="custom"
                stripSpaces={true}
                uppercase={true}
                placeholder={SUBSCRIPTION.FORM.IBAN.PLACEHOLDER}
                defaultValue={ibanField.defaultValue}
                {...field}
                onChange={ibanField.onChange}
                onBlur={ibanField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={bicField.path}
            label={bicField.label}
            required={bicField.required}
            disabled={bicField.disabled}
          >
            {(field) => (
              <Input
                label={bicField.label}
                icon={<MdOutlineQrCode size={16} />}
                type="custom"
                stripSpaces={true}
                placeholder={SUBSCRIPTION.FORM.BIC.PLACEHOLDER}
                defaultValue={bicField.defaultValue}
                {...field}
                onChange={bicField.onChange}
                onBlur={bicField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={isAdherentField.path}
            label={isAdherentField.label}
            required={isAdherentField.required}
            disabled={isAdherentField.disabled}
          >
            {(field) => (
              <Input
                type="radio"
                variant="group"
                icon={<MdOutlinePerson size={16} />}
                value={get(initialValues, isAdherentPath)}
                label={isAdherentField.label}
                values={[
                  {
                    value: true,
                    label:
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                        .OPTIONS.ADHERENT,
                  },
                  {
                    value: false,
                    label:
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                        .OPTIONS.ANOTHER_PERSON,
                    popover:
                      get(
                        initialValues,
                        "insurancePlan.fields.payment.shouldBeAdherent",
                      ) &&
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT
                        .SHOULD_BE_ADHERENT,
                  },
                ]}
                {...field}
                onBlur={isAdherentField.onBlur}
                onChange={isAdherentField.onChange}
              />
            )}
          </FormField>
          {!get(initialValues, isAdherentPath) && (
            <>
              <FormField
                path={ownerTypeField.path}
                label={ownerTypeField.label}
                required={ownerTypeField.required}
                disabled={ownerTypeField.disabled}
              >
                {(field) => (
                  <Input
                    type="radio"
                    variant="group"
                    icon={<MdPeopleOutline icon="man" size={16} />}
                    values={[
                      {
                        value: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
                        label:
                          SUBSCRIPTION.FORM.BANK_ACCOUNT_OWNER_TYPE.OPTIONS
                            .PARTICULAR,
                      },
                      {
                        value: BANK_ACCOUNT_OWNER_TYPE.COMPANY,
                        label:
                          SUBSCRIPTION.FORM.BANK_ACCOUNT_OWNER_TYPE.OPTIONS
                            .COMPANY,
                      },
                    ]}
                    label={ownerTypeField.label}
                    value={get(initialValues, ownerTypePath)}
                    {...field}
                    onChange={ownerTypeField.onChange}
                  />
                )}
              </FormField>
              {get(initialValues, ownerTypePath) ===
              BANK_ACCOUNT_OWNER_TYPE.COMPANY ? (
                <Form
                  type="highlight"
                  title={
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                      .ORGANIZATION.TITLE
                  }
                >
                  <div className="grid--1">
                    <FormField
                      path={socialReasonField.path}
                      label={socialReasonField.label}
                      required={socialReasonField.required}
                      disabled={socialReasonField.disabled}
                    >
                      {(field) => (
                        <Input
                          label={socialReasonField.label}
                          icon={<MdOutlineBadge size={16} />}
                          type="custom"
                          normalizeText={true}
                          placeholder={socialReasonField.label}
                          defaultValue={socialReasonField.defaultValue}
                          {...field}
                          onChange={socialReasonField.onChange}
                          onBlur={socialReasonField.onBlur}
                        />
                      )}
                    </FormField>
                  </div>
                </Form>
              ) : (
                <Form
                  type="highlight"
                  title={
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER
                      .IDENTITY.TITLE
                  }
                >
                  <div className="grid--1">
                    <FormField
                      path={genderField.path}
                      label={genderField.label}
                      required={genderField.required}
                      disabled={genderField.disabled}
                    >
                      {(field) => (
                        <Input
                          type="radio"
                          variant="group"
                          icon={<MdPeopleOutline icon="man" size={16} />}
                          values={[
                            {
                              value: GENDER.MALE,
                              label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                            },
                            {
                              value: GENDER.FEMALE,
                              label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                            },
                          ]}
                          label={genderField.label}
                          value={get(initialValues, genderPath)}
                          {...field}
                          onChange={genderField.onChange}
                          onBlur={genderField.onBlur}
                        />
                      )}
                    </FormField>
                    <FormField
                      path={firstNameField.path}
                      label={firstNameField.label}
                      required={firstNameField.required}
                      disabled={firstNameField.disabled}
                    >
                      {(field) => (
                        <Input
                          label={firstNameField.label}
                          icon={<MdOutlineBadge size={16} />}
                          type="custom"
                          normalizeText={true}
                          placeholder={
                            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT
                              .PAYER.IDENTITY.FIRST_NAME.PLACEHOLDER
                          }
                          defaultValue={firstNameField.defaultValue}
                          {...field}
                          onChange={firstNameField.onChange}
                          onBlur={firstNameField.onBlur}
                        />
                      )}
                    </FormField>
                    <FormField
                      path={lastNameField.path}
                      label={lastNameField.label}
                      required={lastNameField.required}
                      disabled={lastNameField.disabled}
                    >
                      {(field) => (
                        <Input
                          label={lastNameField.label}
                          icon={<MdOutlineBadge size={16} />}
                          type="custom"
                          normalizeText={true}
                          placeholder={
                            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT
                              .PAYER.IDENTITY.LAST_NAME.PLACEHOLDER
                          }
                          defaultValue={lastNameField.defaultValue}
                          {...field}
                          onChange={lastNameField.onChange}
                          onBlur={lastNameField.onBlur}
                        />
                      )}
                    </FormField>
                  </div>
                </Form>
              )}
              <Form
                key={get(initialValues, ownerTypePath)}
                type="highlight"
                title={`${SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PAYMENT.PAYER.ADDRESS.TITLE} (${SUBSCRIPTION.FORM.OPTIONAL})`}
              >
                <div className="grid--1">
                  <FormField
                    path={streetField.path}
                    label={streetField.label}
                    required={streetField.required}
                    disabled={streetField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={streetField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.STREET.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={streetField.defaultValue}
                        {...field}
                        onChange={streetField.onChange}
                        onBlur={streetField.onBlur}
                      />
                    )}
                  </FormField>
                  <FormField
                    path={zipCodeField.path}
                    label={zipCodeField.label}
                    required={zipCodeField.required}
                    disabled={zipCodeField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={zipCodeField.label}
                        icon={<MdOutlinePinDrop size={16} />}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        placeholder={SUBSCRIPTION.FORM.POSTAL_CODE.PLACEHOLDER}
                        defaultValue={zipCodeField.defaultValue}
                        {...field}
                        onChange={zipCodeField.onChange}
                        onBlur={zipCodeField.onBlur}
                      />
                    )}
                  </FormField>
                  <FormField
                    path={cityField.path}
                    label={cityField.label}
                    required={cityField.required}
                    disabled={cityField.disabled}
                  >
                    {(field) => (
                      <Input
                        icon={<MdOutlineSouthAmerica size={16} />}
                        label={cityField.label}
                        placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        defaultValue={cityField.defaultValue}
                        {...field}
                        onChange={cityField.onChange}
                        onBlur={cityField.onBlur}
                      />
                    )}
                  </FormField>
                </div>
              </Form>
            </>
          )}
        </div>
      </Form>
    </div>
  );
};

export default Payment;
